import { BookOpen } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { TemplateFormDialogContentsSectionForm } from "../types";
import { CarePlanItemPicker } from "./CarePlanItemPicker";
import { CarePlanSelectedList } from "./CarePlanSelectedList";
import { CarePlanContentItem } from "./CarePlanContentItem";

export interface CarePlanContentSectionProps {
  form: TemplateFormDialogContentsSectionForm<"carePlanContent">;
}

export const CarePlanContentSection = ({ form }: CarePlanContentSectionProps) => {
  const { t } = useTranslation("calendar");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const contentQuery = useTellescopeInfiniteQuery({
    model: "managed_content_records",
    method: "getSome",
    request: {
      search: debouncedSearch ? { query: debouncedSearch } : undefined,
    },
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });

  const selectedIds = useMemo(() => new Set(form.values.carePlanContent ?? []), [form.values.carePlanContent]);
  const content = useMemo(() => contentQuery.data ?? [], [contentQuery.data]);
  const isLoading = contentQuery.isFetching || search !== debouncedSearch;

  const selectableContent = useMemo(
    () =>
      content.reduce<TellescopeModelFromName<"managed_content_records">[]>((acc, curr) => {
        if (selectedIds.has(curr.id)) {
          return acc;
        }
        acc.push(curr);
        return acc;
      }, []),
    [content, selectedIds]
  );

  const handleAdd = (id: string) => {
    if (selectedIds.has(id)) return;
    form.setFieldValue("carePlanContent", [...selectedIds, id]);
    setSearch("");
    setPopoverOpen(false);
  };

  const handleRemove = (id: string) => {
    form.setFieldValue(
      "carePlanContent",
      Array.from(selectedIds).filter((c) => c !== id)
    );
  };

  return (
    <div className="space-y-4">
      <CarePlanItemPicker
        icon={<BookOpen size={16} />}
        label={t("Educational Content")}
        addButtonText={t("Add Content")}
        searchPlaceholder={t("Search content...")}
        emptyText={t("No content found.")}
        searchValue={search}
        onSearchChange={setSearch}
        isLoading={isLoading}
        popoverOpen={popoverOpen}
        onPopoverOpenChange={setPopoverOpen}
        items={selectableContent}
        getItemId={(c) => c.id}
        onSelectItem={handleAdd}
        renderItem={(c) => c.title}
      />
      <CarePlanSelectedList
        ids={form.values.carePlanContent ?? []}
        emptyText={t("No content selected. Use the dropdown above to add content.")}
        onRemove={handleRemove}
        ItemComponent={CarePlanContentItem}
      />
    </div>
  );
};
